/* eslint-disable no-unused-vars */
/* global __DEV__ */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import PathUtils from 'history/PathUtils';
import { IntlProvider, addLocaleData } from 'react-intl';
import ru from 'react-intl/locale-data/ru';
import uk from 'react-intl/locale-data/uk';
import metaBuilder from 'metaBuilder';
import * as Cookies from 'js-cookie';
import * as Sentry from '@sentry/react';
import App from 'shared/components/App';
import { fireRTBPageView } from 'shared/helpers/rtbHouse';

// For vendor.js
import Select from 'react-select';
import ShareBtn from 'react-share';
import Buffer from 'buffer';
import CoreJs from 'core-js';
import LazyLoad from 'react-lazyload';
import reactSticky from 'react-sticky';
import inputMask from 'react-input-mask';
import reactScroll from 'react-scroll';

import createFetch from '../createFetch';
import configureStore from '../shared/store/configureStore';
import rootReducer from './reducers';
import { setFingerprint } from '../shared/actions/globals';
import history from '../history';
import {
  updateMeta,
  updateAlternateLink,
  updateLink,
  deleteLink,
  deleteTag,
  updateCustomMeta,
} from '../DOMUtils';
import createRouter from '../createRouter';
import routes from '../desktop/routes';
import { AVAILABLE_LOCALES } from '../shared/constants/locale';
import { curriedTranslate, removeLocaleFromHref, toLocalized } from '../shared/helpers/locale';
import { disableReactIntlLocaleErrors } from '../shared/helpers/common';

if (!__DEV__ && window.App.sentryDSN) {
  Sentry.init({ dsn: window.App.sentryDSN });
}

const originalConsoleError = console.error;

console.error = (...args) => {
  const [firstArg] = args;
  if (
    typeof firstArg === 'string' &&
    (firstArg.includes('[React Intl] Cannot format message') ||
      firstArg.includes('Missing message:'))
  ) {
    console.warn(...args);
  } else {
    originalConsoleError(...args);
  }
};

disableReactIntlLocaleErrors(console);
const router = createRouter(routes);
const fetch = createFetch(self.fetch, {
  baseUrl: window.App.apiUrl,
  /* Basic authorization  */
  defaultAuth: '' /* window.App.defaultAuth */,
});

// GTM

// Intl
const { locale, dealerSlug } = window.App.state.globals;
let messages = {};
let translate = null;
let metaTegs = {};

const loadLocalePromise = (async () => {
  messages = await import(`../locale/${locale}.json`);
  metaTegs = await import(`../locale/metaTegs/${locale}.json`);
  translate = curriedTranslate({ ...messages, ...metaTegs });
})();

/* eslint-disable global-require */

/* cookies for Rete scripts */
Cookies.set('rete-force-tracking', 'true');

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const store = configureStore(window.App.state, rootReducer, { history, fetch });
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // Universal HTTP client
  fetch,
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store,
  storeSubscription: null,
};

// Locale
addLocaleData([...ru, ...uk]);
window.App.dispatch = store.dispatch;
window.App.getState = store.getState;
if (!window.AbStor) window.AbStor = {};
// Fingerprint
store.dispatch(setFingerprint());

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

let onRenderComplete = function initialRenderComplete() {
  const elem = document.getElementById('css');
  if (elem && elem.parentNode) elem.parentNode.removeChild(elem);
  onRenderComplete = function renderComplete(route, location) {
    document.title = route.title;
    updateCustomMeta('og:title', route.title);
    updateMeta('description', route.description);
    updateCustomMeta('og:description', route.description);

    const {
      isAdvertPage,
      pageNumber,
      shouldSetIndex,
      shouldSetNoIndex,
      stringifiedPrevQuery,
      stringifiedNextQuery,
      stringifiedQuery,
    } = metaBuilder(route);

    const { protocol, host } = window.location;
    const origin = `${protocol}//${host}`;
    const routeUrl = `${route.url || ''}${isAdvertPage ? '' : '/'}`.replace('//', '/');

    // set or remove 'next' and 'prev' meta links
    if (pageNumber && stringifiedNextQuery) {
      updateLink('next', `${origin}${routeUrl}${stringifiedNextQuery}`);
    } else {
      deleteLink('next');
    }
    if (pageNumber && pageNumber !== 1) {
      updateLink('prev', `${origin}${routeUrl}${stringifiedPrevQuery}`);
    } else {
      deleteLink('prev');
    }
    // set or remove 'noindex' meta links
    if (shouldSetNoIndex) {
      updateMeta('robots', 'noindex, nofollow');
    } else if (shouldSetIndex) {
      updateMeta('robots', 'index, follow');
    } else {
      deleteTag('meta', 'name', 'robots');
    }
    // set or remove 'alternate' meta links
    AVAILABLE_LOCALES.forEach(lang =>
      updateAlternateLink(
        lang,
        `${origin}${toLocalized(removeLocaleFromHref(routeUrl), lang)}${stringifiedQuery}`,
      ),
    );

    updateCustomMeta('og:url', `${origin}${routeUrl}`);
    // Update necessary tags in <head> at runtime here, ie:
    // updateMeta('keywords', route.keywords);
    updateCustomMeta('og:image', route.ogImage);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (
          target &&
          target.getBoundingClientRect &&
          typeof target.getBoundingClientRect === 'function' &&
          target.getBoundingClientRect() &&
          target.getBoundingClientRect().top
        ) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);

    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    if (window.ga && PathUtils) {
      window.ga('send', 'pageview', PathUtils.createPath(location));
    }
  };
};

const container = document.getElementById('app');
let appInstance;
let currentLocation = history.location;

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  // Clear branding
  const body = document.getElementsByTagName('body')[0];
  body.style.cssText = {};
  const brandingLink = document.getElementById('takeover_Skin');
  if (brandingLink) {
    brandingLink.remove();
  }

  const pageWrapperEl = document.getElementById('pageWrapperId');
  if (pageWrapperEl) {
    pageWrapperEl.style.cssText = {};
  }

  const fakeSubHeader = document.getElementById('fakeSubHeader');
  if (fakeSubHeader) {
    fakeSubHeader.style.cssText = {};
  }

  await loadLocalePromise;

  try {
    /*
    Далі описан брудний хак, який потрібен для роботоздатності персональних субдоменів салонів и фірм.
    Персональні сторінки салонів доступні по шляху /<dealer_slug>/. Для кожного салону ми заводимо
    субдомен. Таким чином, нам потрібно щоб сайт салона був за адресою <dealer_slug>.ab.ua, а не за адресою
    <dealer_slug>.ab.ua/<dealer_slug>.
    Для цього в конфігі субдомену для `location /` прописан proxy_pass на /<dealer_slug>. І все працює добре, але
    спочатку віддається потрібна сторінка салону, а потім з'являється інша сторінка ab.ua.
    На прикладі сторінки about: спочатку ми бачимо сторінку /<dealer_slug>/about, а потім /about.
    Пролема в тому, що першу сторінку віддає сервер. На сервер потрапляє правильний шлях, який описан в proxy_pass.
    Далі спрацьовує роутинг клієнта, але урл в нас не /<dealer_slug>/about, а /about. Тому і завантажується
    зовсім інша сторінка.

    Для вирішення цієї проблеми було зроблено наступний хак:
    * в конфігі nginx додається header dealer-slug з потрібним слагом салона;
    * в src/server.js цей хідер записується в store.globals;
    * нижче в коді цей слаг додається до поточного шляху, щоб визначити корректний клієнтський роут.
    * на сторінках салонів about, blog, advert, news, при наявності в сторі dealerSlug, з поточного урла
    прибирається слаг салона, щоб усі посилання були /about, а не <dealer_slug>/about.
    */
    const dealerRootPath = dealerSlug ? `/${dealerSlug}` : '';

    // Когда открывается страница под не "родным" доменом, скрипт не должен выполнятся.
    // Сделано, чтобы избежать редиректа, описание проблемы: https://github.com/remix-run/react-router/issues/5801
    const host = window && window.location && window.location.host;
    const appHost = window && window.App && window.App.host;

    if (host !== appHost && host !== 'avtobazar.ua') return false;

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const locationPath =
      location.pathname && location.pathname.startsWith(dealerRootPath)
        ? location.pathname
        : `${dealerRootPath}${location.pathname}`;
    const route = await router.resolve({
      ...context,
      path: locationPath,
      query: queryString.parse(location.search),
      translate,
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return true;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return true;
    }

    fireRTBPageView(locationPath);

    appInstance = ReactDOM.hydrate(
      <IntlProvider locale={locale} messages={{ ...messages, ...metaTegs }}>
        <App context={context}>
          {route.component}
        </App>
      </IntlProvider>,
      container,
      () => onRenderComplete(route, location),
    );
  } catch (error) {
    const errorMesg = (error && error.messages) || '';
    if (__DEV__) {
      throw error;
    }

    console.error(errorMesg);

    // Do a full page reload if error occurs during client-side navigation
    if (action && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
  return false;
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./routes', () => {
    if (appInstance) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}



// WEBPACK FOOTER //
// ./src/desktop/client.js